import { SeoHead } from "@/components/SeoHead";
import { PageTransition } from "@/components/layout/PageTransition";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, GraduationCap, Users, Trophy, Target, Clock, Globe, LucideIcon } from "lucide-react";
import { useCms } from "@/hooks/useCms";

const ICON_MAP: Record<string, LucideIcon> = {
  Trophy, Target, Users, Clock, Globe, GraduationCap,
};

const DEFAULT_FEATURES = [
  { icon: "Trophy", title: "Eliada Coaching Staff", desc: "Learn from UEFA-licensed coaches with experience in professional clubs across Europe and Africa." },
  { icon: "Target", title: "Technical Development", desc: "Focused programs on ball control, dribbling, passing accuracy, shooting, and positional awareness." },
  { icon: "Users", title: "Team & Tactical Play", desc: "Group training sessions emphasizing formation understanding, pressing, transitions, and match intelligence." },
  { icon: "Clock", title: "Physical Conditioning", desc: "Sport science-backed fitness programs improving speed, endurance, strength, and injury prevention." },
  { icon: "Globe", title: "International Exposure", desc: "Participation in regional and international tournaments, giving players visibility with global scouts." },
  { icon: "GraduationCap", title: "Academic Balance", desc: "We work alongside schools to ensure athletes maintain academic progress alongside their football development." },
];

const DEFAULT_BULLETS = [
  "Ages 8–25 accepted across different program tiers",
  "Residential and day programs available",
  "Regular intra-squad and inter-academy competitions",
  "Video analysis and performance monitoring",
  "Nutrition and wellness guidance",
];

export default function FootballAcademy() {
  const { getMeta, getText } = useCms();
  const meta = getMeta<{
    heading?: string;
    highlight?: string;
    subtext?: string;
    heroImage?: string;
    overviewHeading?: string;
    overviewText?: string;
    overviewImage?: string;
    bullets?: string[];
    features?: { icon: string; title: string; desc: string }[];
  }>("service_academy", {});

  const heading = meta.heading ?? "FOOTBALL";
  const highlight = meta.highlight ?? "ACADEMY";
  const subtext = meta.subtext ?? getText("service_academy", "Structured, professional training programs designed to develop complete footballers — technically, tactically, physically, and mentally.");
  const heroImage = meta.heroImage || "https://images.unsplash.com/photo-1543351611-58f69d7c1781?w=1600&auto=format&fit=crop";
  const overviewHeading = meta.overviewHeading ?? "WHAT IS THE ELIADA ACADEMY?";
  const overviewText = meta.overviewText ?? "The Eliada Sports Football Academy is a professional development program built on the foundations of elite European football methodology. We run structured programs across our African and European facilities, providing young athletes with a pathway from grassroots to professional football.\n\nOur curriculum is designed to produce well-rounded players who can compete at club, national, and international levels. Every athlete receives personalized attention, performance tracking, and regular evaluations.";
  const overviewImage = meta.overviewImage || "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=900&auto=format&fit=crop";
  const bullets = (Array.isArray(meta.bullets) && meta.bullets.length > 0) ? meta.bullets : DEFAULT_BULLETS;
  const features = (Array.isArray(meta.features) && meta.features.length > 0) ? meta.features : DEFAULT_FEATURES;

  return (
    <>
      <SeoHead
        title={`${heading} ${highlight}`}
        description={subtext}
        path="/services/football-academy"
      />
      <PageTransition className="pt-24 pb-0">
        {/* Hero */}
        <div className="relative min-h-[55vh] flex items-center overflow-hidden">
          <img src={heroImage} alt={`${heading} ${highlight}`} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/40 bg-primary/10 text-primary mb-6">
              <GraduationCap size={14} />
              <span className="text-xs font-display tracking-widest uppercase font-semibold">Our Services</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-display font-bold text-white mb-6 max-w-2xl leading-tight">
              {heading} <span className="text-primary">{highlight}</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-xl mb-8 leading-relaxed">
              {subtext}
            </p>
            <Link href="/join" onClick={() => {}}>
              <Button size="lg" className="gap-2">Apply Now <ArrowRight size={18} /></Button>
            </Link>
          </div>
        </div>

        {/* Overview */}
        <section className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
                  {overviewHeading}
                </h2>
                <div className="space-y-4 mb-8">
                  {overviewText.split("\n\n").map((para, i) => (
                    <p key={i} className="text-gray-600 text-lg leading-relaxed">
                      {para}
                    </p>
                  ))}
                </div>
                <ul className="space-y-3">
                  {bullets.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-gray-700">
                      <CheckCircle className="text-primary shrink-0 w-5 h-5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <img src={overviewImage} alt="Training" className="rounded-2xl shadow-2xl w-full object-cover" />
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-24 bg-secondary">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
                WHAT WE <span className="text-primary">OFFER</span>
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Every dimension of football development covered under one roof.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((f, i) => {
                const IconComponent = ICON_MAP[f.icon] || Trophy;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-background p-8 rounded-2xl border border-white/5 hover:border-primary/40 transition-colors"
                  >
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-5">
                      <IconComponent className="text-primary w-6 h-6" />
                    </div>
                    <h3 className="font-display font-bold text-white text-lg mb-3">{f.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">READY TO JOIN THE ACADEMY?</h2>
            <p className="text-xl text-gray-800 mb-10 font-medium">Submit your application today and take your first step toward a professional football career.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link href="/join" onClick={() => {}}><Button size="lg" className="bg-gray-900 text-white hover:bg-black">Apply Now</Button></Link>
              <Link href="/contact" onClick={() => {}}><Button size="lg" variant="outline" className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white bg-transparent">Contact Us</Button></Link>
            </div>
          </div>
        </section>
      </PageTransition>
    </>
  );
}
